import { useEffect, useRef, useState, useContext, memo } from "react";
import ImageLayer from "ol/layer/Image";
import ImageWMS from "ol/source/ImageWMS";

import { MapContext } from "../../providers/mapProvider";
import { useProjection } from "../../hooks/useProjection";

const CadastralOverlay = ({
  visible = false,
  opacity = 1,
  zIndex = 10,
  applyOffset,
}) => {
  const { map } = useContext(MapContext);
  const cadastralLayerRef = useRef(null);
  const { projection } = useProjection();

  const [layerConfig] = useState({
    parcels: true,
    buildings: true,
    numbers: false,
    boundaries: true,
  });

  useEffect(() => {
    if (!map) return;

    if (cadastralLayerRef.current) {
      map.removeLayer(cadastralLayerRef.current);
      cadastralLayerRef.current = null;
    }

    const layers = [];
    if (layerConfig.parcels) layers.push("prehledka_kat_uz");
    if (layerConfig.boundaries) layers.push("hranice_parcel");
    if (layerConfig.buildings) layers.push("DEF_BUDOVY");

    const cadastralLayer = new ImageLayer({
      source: new ImageWMS({
        url: "https://services.cuzk.gov.cz/wms/wms.asp",
        params: {
          LAYERS: layers.join(","),
          TRANSPARENT: true,
        },
        projection,
        serverType: "mapserver",
        attributions: "Czech Office for Surveying, Mapping and Cadastre (ČÚZK)",
        crossOrigin: "anonymous",
        imageLoadFunction: function (image, src) {
          /**
           * The cadastre API is giving results off by 7m and 9m
           * if the tiff uses older ESPG:5514 projection we needed to move it
           *
           * if the tiff uses newer projection ESPG:8353 we just have to adjust by 1m
           */
          const offset = applyOffset ? { x: -7.6, y: -9.2 } : { x: 1, y: 1 };
          const url = new URL(src);

          const bbox = url.searchParams.get("BBOX");
          if (bbox) {
            const bboxArray = bbox.split(",").map(Number);

            const offsetBbox = [
              bboxArray[0] - offset.x,
              bboxArray[1] - offset.y,
              bboxArray[2] - offset.x,
              bboxArray[3] - offset.y,
            ];

            url.searchParams.set("BBOX", offsetBbox.join(","));
          }

          image.getImage().src = url.toString();
        },
      }),
      opacity: opacity,
      visible: visible,
      zIndex: zIndex,
    });

    map.addLayer(cadastralLayer);
    cadastralLayerRef.current = cadastralLayer;

    const viewChangeHandler = () => {
      if (cadastralLayerRef.current && visible) {
        cadastralLayerRef.current.getSource().refresh();
      }
    };

    map.getView().on("change:center", viewChangeHandler);
    map.getView().on("change:resolution", viewChangeHandler);

    return () => {
      if (cadastralLayerRef.current) {
        map.removeLayer(cadastralLayerRef.current);
        cadastralLayerRef.current = null;
      }
      map.getView().un("change:center", viewChangeHandler);
      map.getView().un("change:resolution", viewChangeHandler);
    };
  }, [map, layerConfig, visible, zIndex, opacity, applyOffset, projection]);

  return null;
};

export default memo(CadastralOverlay);
